import CarouselLeftArrowIcon from "@/assets/campaign/FFFFFF_carousel_left_arrow.svg?react";
import CarouselRightArrowIcon from "@/assets/campaign/FFFFFF_carousel_right_arrow.svg?react";
import CloseIcon from "@/assets/colorable/close_x.svg?react";
import CarouselHeader from "@/components/Campaign/management/components/CarouselHeader";
import CarouselItem from "@/components/Campaign/management/components/CarouselItem";
import {useAdminCarouselStore, useCarouselStore} from "@/store/carousel";
import {isAdminFeedbackModalOpenAtom} from "@/store/jotaiAtoms";
import {ModalContext} from "@/store/modalContext";
import {useAtomValue} from "jotai";
import last from "lodash/last";
import {FC, useContext, useEffect, useId, useMemo, useRef, useState} from "react";
import {Portal} from "react-portal";
import Slider, {Settings} from "react-slick";
import {useKeyPressEvent, useMedia} from "react-use";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

const config: Settings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: false,
};

const Carousel: FC = () => {
  const isWide = useMedia("(min-width: 1024px)");
  const sliderRef = useRef<Slider>(null);

  const isAdminFeedbackModalOpen = useAtomValue(isAdminFeedbackModalOpenAtom);
  const carouselStore = isAdminFeedbackModalOpen ? useAdminCarouselStore : useCarouselStore;

  const id = useId();
  const {modalIds, setModalId, deleteModalId} = useContext(ModalContext);
  useEffect(() => {
    setModalId(id);

    return () => {
      deleteModalId(id);
    };
  }, []);
  const isLastModal = useMemo(() => last(modalIds) === id, [modalIds, id]);
  const currentMedia = carouselStore(state => state.currentMedia);
  const close = carouselStore(state => state.reset);
  useKeyPressEvent("Escape", () => {
    if (isLastModal) close();
  });
  useKeyPressEvent("ArrowLeft", () => {
    if (isLastModal) sliderRef.current?.slickPrev();
  });
  useKeyPressEvent("ArrowRight", () => {
    if (isLastModal) sliderRef.current?.slickNext();
  });
  const initialMediaIndex = carouselStore(state => state.initialMediaIndex);
  const shouldUsePersistedIndex = carouselStore(state => state.shouldUsePersistedIndex);
  const persistedCurrentIdx = carouselStore(state => state.currentIndex);
  const setPersistedCurrentIdx = carouselStore(state => state.setCurrentIndex);
  const [currentIdx, setCurrentIdx] = useState(initialMediaIndex);
  const currentMediaIndex = useMemo(
    () => (shouldUsePersistedIndex ? persistedCurrentIdx : currentIdx),
    [shouldUsePersistedIndex, persistedCurrentIdx, currentIdx],
  );
  const setCurrentMediaIndex = useMemo(
    () => (shouldUsePersistedIndex ? setPersistedCurrentIdx : setCurrentIdx),
    [shouldUsePersistedIndex, setPersistedCurrentIdx, setCurrentIdx],
  );

  return (
    <Portal node={document && document.getElementById("root")}>
      <div
        style={{
          zIndex: 9999 + modalIds.findIndex(modalId => modalId === id),
        }}
        className="fixed top-1/2 left-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2 bg-black/90 p-8 shadow-lg">
        <div className="relative">
          <CarouselHeader
            currentMedia={currentMedia[currentMediaIndex]}
            currentMediaIndex={currentMediaIndex}
            mediaLength={currentMedia.length}
          />
          <button onClick={close} className="absolute top-0 right-0 z-10">
            <CloseIcon className="text-neutral-grey-light hover:text-neutral-grey-dark h-5 w-5 transition-colors duration-150" />
          </button>
        </div>

        <div className="flex h-full items-center justify-center">
          {currentMedia.length > 1 && (
            <button
              onClick={() => sliderRef.current?.slickPrev()}
              className={"absolute top-1/2 z-20 " + (isWide ? "left-40" : "left-4")}>
              <CarouselLeftArrowIcon className={isWide ? "h-16 w-16" : "h-8 w-8"} />
            </button>
          )}
          <Slider
            afterChange={setCurrentMediaIndex}
            className="w-[60vw] max-w-[70%]"
            arrows={false}
            {...config}
            ref={sliderRef}
            initialSlide={initialMediaIndex}>
            {currentMedia.map((content, index) => (
              <CarouselItem key={index} content={content} isCurrentlyVisible={index === currentMediaIndex} />
            ))}
          </Slider>
          {currentMedia.length > 1 && (
            <button
              onClick={() => sliderRef.current?.slickNext()}
              className={"absolute top-1/2 z-20 " + (isWide ? "right-40" : "right-4")}>
              <CarouselRightArrowIcon className={isWide ? "h-16 w-16" : "h-8 w-8"} />
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default Carousel;
